import { useMemo, useState } from "react";
import { formatPrice } from "@/utils/utils";
import { handleApiError } from "@/helpers/apiErrorHelper";
import useAppDispatch from "@/hooks/useAppDispatch";
import { shippingMethodIdToNameMap, shippingMethodToTypeMap } from "@/constants/mapping";
import wooStoreApiService from "@/api/services/wooStoreApiService";
import { ShippingMethodType, type ShippingMethodId } from "@/constants/enums";
import type { Cart, ShippingRateMethod } from "@/types/cart";

//#region Types
interface ShippingOptions {
  id: string;
  title: string;
  description: string;
  addition: string;
}

export interface UseShippingMethodsReturn {
  isShippingMethodsLoading: boolean;
  shippingOptions: ShippingOptions[];
  getShippingMethodType: (
    shippingMethodIdField: ShippingMethodId,
  ) => ShippingMethodType | undefined;
  updateSelectedShippingMethod: (methodId: ShippingMethodId) => Promise<void>;
}
//#endregion

const useShippingMethods = ({ cart }: { cart: Cart | null }) => {
  const appDispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const shippingOptions = useMemo(() => {
    const shippingMethods: ShippingRateMethod[] = cart?.shipping_rates?.[0]?.shipping_rates || [];

    return shippingMethods.map((method) => ({
      id: method.method_id,
      title: shippingMethodIdToNameMap[method.method_id] || method.name,
      description: method.description,
      addition: `${formatPrice(method.price)} ${parseFloat(method.price) === 0 ? "免運費" : ""}`,
    }));
  }, [cart?.shipping_rates]);

  const getShippingMethodType = (shippingMethodId: ShippingMethodId) => {
    return shippingMethodToTypeMap[shippingMethodId] ?? ShippingMethodType.Invaild;
  };

  const updateSelectedShippingMethod = async (methodId: ShippingMethodId) => {
    setIsLoading(true);

    const shippingPackage = cart?.shipping_rates?.[0];
    const shippingRateId = shippingPackage?.shipping_rates.find(
      (rate) => rate.method_id === methodId,
    )?.rate_id;

    const payload = {
      package_id: shippingPackage?.package_id ?? 0,
      rate_id: shippingRateId ?? "",
    };

    try {
      const { data: cartData } = await wooStoreApiService.cart.selectShippingRate(payload);
      appDispatch.setFullCart(cartData);
    } catch (error) {
      handleApiError(error, appDispatch, "無法更新運送方式，請稍後再試");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isShippingMethodsLoading: isLoading,
    shippingOptions,
    getShippingMethodType,
    updateSelectedShippingMethod,
  };
};

export default useShippingMethods;
